import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import axiosInstance from "src/common/axiosInstance";
import ModalHeader from "./ModalHeader";
import OwnersToolbar from "./OwnersToolbar";
import StageAssignmentTable from "./StageAssignmentTable";
import ModalFooter from "./ModalFooter";
import LoadingSkeleton from "./LoadingSkeleton";
import EmptyState from "./EmptyState";
import { getDevelopers } from "src/features/MiniCRM/CachedData";

export default function LeadOwnersModal({
  isOpen,
  onClose,
  leadId,
  workspaceId,
  owners,
  fetchOwners,
  stages,
  loadingStages,
}) {
  const [editedOwners, setEditedOwners] = useState([]);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setEditedOwners(
      owners.map((owner) => ({
        ...owner,
        stage_ids: [...owner.stage_ids],
      }))
    );
  }, [isOpen, leadId, owners]);

  const handleModalSave = async () => {
  // 1. Validate stages first
  if (
    editedOwners.length > 0 &&
    editedOwners.some(
      (owner) => !owner.stage_ids || owner.stage_ids.length === 0
    )
  ) {
    toast.error(
      "Please assign at least one stage to every owner before saving."
    );
    return;
  }

  try {
    // 2. Get the latest workspace users
    const workspaceUsers = await getDevelopers(workspaceId);

    const validUserIds = new Set(
      (workspaceUsers || []).map(
        (user) => user.user_id
      )
    );

    // 3. Find owners that no longer exist
    const invalidOwners = editedOwners.filter(
      (owner) => !validUserIds.has(owner.user_id)
    );

    // 4. Keep only valid owners
    const validOwners = editedOwners.filter(
      (owner) => validUserIds.has(owner.user_id)
    );

    // 5. If invalid owners exist, tell the user
    if (invalidOwners.length > 0) {
      toast.warning(
        `${invalidOwners.length} owner${
          invalidOwners.length > 1 ? "s" : ""
        } no longer exist and ${
          invalidOwners.length > 1
            ? "were"
            : "was"
        } removed.`
      );
    }

    // 6. Build payload using only valid owners
    const payload = {
      owners: validOwners.map((owner) => ({
        user_id: owner.user_id,
        stage_ids: owner.stage_ids,
      })),
    };

    // 7. Save
    await axiosInstance.put(`/leads/${leadId}/owners`, payload);

    // 8. Update local state so deleted owner disappears
    setEditedOwners(validOwners);

    // 9. Refresh owners
    await fetchOwners();

    // 10. Close modal
    onClose();

    // Don't show the normal success toast if an invalid owner was already reported.
    if (invalidOwners.length === 0) {
      toast.success("Lead owners updated successfully.");
    }
  } catch (err) {
    console.error(err);
    toast.error("Failed to update lead owners.");
  }
};

  const handleAddOwner = (selectedUser) => {
    // Prevent duplicate owners
    if (editedOwners.some((owner) => owner.user_id === selectedUser.user_id)) {
      return;
    }

    const newOwner = {
      user_id: selectedUser.user_id,
      user: {
        id: selectedUser.user.id,
        name: selectedUser.user.name,
        email: selectedUser.user.email,
      },
      stage_ids: [], // no stages selected initially
      isNew: true,
    };

    setEditedOwners((prev) => [...prev, newOwner]);
  };

  // ESC key closes the modal.
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e) {
      if (e.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Move initial focus into the dialog when it opens.
  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const hasOwners = editedOwners.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-theme-950/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-owners-modal-title"
        tabIndex={-1}
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-md focus:outline-none"
      >
        <ModalHeader onClose={onClose} />

        <div className="flex-1 overflow-y-auto px-6 py-6 sm:px-8">
          <OwnersToolbar onAddOwner={handleAddOwner} workspaceId={workspaceId}/>

          {loadingStages ? (
            <LoadingSkeleton />
          ) : !hasOwners ? (
            <EmptyState onAddOwner={handleAddOwner} />
          ) : (
            <StageAssignmentTable
              stages={stages}
              owners={editedOwners}
              setOwners={setEditedOwners}
            />
          )}
        </div>

        <ModalFooter
          onCancel={onClose}
          onSave={handleModalSave}
        />

      </div>
    </div>
  );
}
